import * as React from "react";

// MUI
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { green, grey } from "@mui/material/colors";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

// Internal
import { PublicationType } from "../data/types";

interface PublicationGridCardProps {
  publication: PublicationType;
}

const MEDIA_HEIGHT = 200;

function PublicationGridCard({ publication }: PublicationGridCardProps) {
  const hasImage = !!publication.coverMedia?.url;
  const authorName = publication.user.profile.nickname;
  const category = publication.tags?.[0]?.name || publication.kind;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "white",
        border: 1,
        borderColor: grey[300],
        borderRadius: 1,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease-in-out",
        "&:hover": { boxShadow: 4 },
      }}
    >
      {/* Media / content preview */}
      <Box
        sx={{
          height: MEDIA_HEIGHT,
          overflow: "hidden",
          backgroundColor: grey[100],
          position: "relative",
        }}
      >
        {hasImage ? (
          <img
            alt={publication.title}
            src={publication.coverMedia.url}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              p: 1.5,
              fontSize: "0.5rem",
              lineHeight: 1.4,
              textAlign: "justify",
              color: grey[700],
              "& *": { fontSize: "inherit !important", margin: 0 },
            }}
            dangerouslySetInnerHTML={{ __html: publication.htmlContent || "" }}
          />
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pb: 1,
            borderBottom: 1,
            borderColor: grey[200],
          }}
        >
          {publication.user.profile.photo?.url ? (
            <Avatar
              alt={authorName}
              src={publication.user.profile.photo.url}
              sx={{ width: 40, height: 40, mt: -2.5, border: "2px solid white" }}
            />
          ) : (
            <Avatar sx={{ width: 40, height: 40, mt: -2.5, border: "2px solid white", bgcolor: green[500] }}>
              <AccountCircleIcon />
            </Avatar>
          )}
          <Typography
            variant="caption"
            sx={{
              ml: 1,
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              flexGrow: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {authorName}
          </Typography>
          {category && (
            <Typography variant="caption" sx={{ color: grey[600], ml: 1 }}>
              {category}
            </Typography>
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{ mt: 1, fontWeight: "bold", lineHeight: 1.2 }}
        >
          {publication.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: grey[600], textTransform: "uppercase" }}
        >
          By {authorName}
        </Typography>
      </Box>
    </Box>
  );
}

export default PublicationGridCard;
